import React from "react";
import { convertDateFormat } from "Utils/formatting";
import useAuthStore from "Store/useAuthStore";
import Permission from "Components/Permission";
import {
    JobsTableWrapperStyled,
    SbJobsCodeViewStyled,
    ExpandArrowStyled,
    NoExpandDashStyled,
} from "./JobsLibraryV2.styles";
import SbTable from "uiCore/SbTable/SbTable";
import SbCheckbox from "uiCore/SbCheckbox/SbCheckbox";
import { SbTooltip, FilterOutlined } from "uiCore";

// PRD §5.1 — tooltip content for Job Code view
const TOOLTIP_EXPAND_COLLAPSE = "Show or hide all requisitions under this job code.";
const TOOLTIP_CRITICAL_ROLE = "Mark this role as business-priority. Priority roles are prioritised in skills planning and highlighted in the library.";

const JobsCodeViewTableV2 = ({
    filteredJobs,
    loading,
    handleRowClick,
    handleCheckboxChange,
    expandedRow,
    toggleExpand,
}) => {
    const orgMetaData = useAuthStore((s) => s.orgMetaData);

    // Code view uses the 'job_code' section of ui_elements.
    // The API returns fields in two logical groups within that section:
    //   outerFields [0–5]  → outer table columns (Job Code, Legal Entity, …, expand)
    //   nestedFields [6+]  → nested requisition table columns (REQ #, Role Name, …)
    // Using find-by-type instead of a hardcoded index so column labels remain correct
    // even if the server reorders the ui_elements array.
    const allFields = orgMetaData?.ui_elements
        ?.find(el => el.type === 'job_code')
        ?.field_details?.fields ?? [];
    const outerFields = allFields.slice(0, 6);
    const nestedFields = allFields.slice(6);

    const getFilters = (key) =>
        [...new Set((filteredJobs || []).map((item) => item[key]).filter(Boolean))]
            .sort((a, b) => {
                const aNum = /^\d/.test(a);
                const bNum = /^\d/.test(b);
                if (aNum && !bNum) return -1;
                if (!aNum && bNum) return 1;
                return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
            })
            .map((value) => ({ text: value, value }));

    // ── Main columns ──────────────────────────────────────────────────────────

    const columns = [
        {
            title: outerFields[0]?.label,
            dataIndex: "job_code",
            filters: getFilters("job_code"),
            onFilter: (value, record) => record.job_code === value,
        },
        {
            title: outerFields[1]?.label,
            dataIndex: "legal_entity",
            filters: getFilters("legal_entity"),
            onFilter: (value, record) => record.legal_entity === value,
        },
        {
            title: outerFields[2]?.label,
            dataIndex: "business_unit",
            filters: getFilters("business_unit"),
            onFilter: (value, record) => record.business_unit === value,
        },
        {
            title: outerFields[3]?.label,
            dataIndex: "role_type",
            filters: getFilters("role_type"),
            onFilter: (value, record) => record.role_type === value,
        },
        {
            title: outerFields[4]?.label,
            dataIndex: "job_level",
            filters: getFilters("job_level"),
            onFilter: (value, record) => record.job_level === value,
        },
        {
            title: outerFields[5]?.label,
            align: "center",
            render: (_, record) => {
                const hasReqs = record.requisition_groups?.length > 0;
                if (!hasReqs) return <NoExpandDashStyled>–</NoExpandDashStyled>;
                const isExpanded = expandedRow === record.job_code;
                return (
                    <SbTooltip content={TOOLTIP_EXPAND_COLLAPSE}>
                        <ExpandArrowStyled
                            className={isExpanded ? "up" : "down"}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(record.job_code);
                            }}
                        />
                    </SbTooltip>
                );
            },
        },
    ];

    // ── Nested requisition table ───────────────────────────────────────────────

    const expandedRowRender = (record) => {
        const nestedColumns = [
            { title: "", dataIndex: "_spacer", width: 24, render: () => null },
            {
                title: nestedFields[0]?.label,
                dataIndex: "requisition_id",
                render: (val) => val || "",
            },
            {
                title: nestedFields[1]?.label,
                render: (_, req) => req.job_title_for_jd || record.job_title || record.alt_role_title || "",
            },
            {
                title: nestedFields[2]?.label,
                dataIndex: "function",
                render: (val) => val || "",
            },
            {
                title: nestedFields[3]?.label,
                dataIndex: "sub_function",
                render: (val) => val || "",
            },
            {
                title: nestedFields[4]?.label,
                render: (_, req) => {
                    if (!req.last_updated_time_stamp) return "";
                    const { date, time } = convertDateFormat(req.last_updated_time_stamp);
                    return [
                        req.version ? `v${req.version} -` : "",
                        date, "at", time, " - ", req.current_owner || "",
                    ].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
                },
            },
            {
                title: nestedFields[5]?.label,
                align: "center",
                render: (_, req) => (
                    <Permission resource="jobs_library" actions={["mark_critical_role"]}>
                        <SbTooltip content={TOOLTIP_CRITICAL_ROLE}>
                            <SbCheckbox
                                checked={req.critical_role === true || req.critical_role === "true"}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleCheckboxChange(e, req.sb_unique_id)}
                            />
                        </SbTooltip>
                    </Permission>
                ),
            },
        ];

        // Wrap in RequisitionTableWrapperStyled so the green-tint theme applies
        // to thead/tbody without bleeding into the outer table
        return (
            <SbJobsCodeViewStyled>
                <SbTable
                    columns={nestedColumns}
                    dataSource={record.requisition_groups || []}
                    rowKey={(req, idx) => req.sb_unique_id ?? idx}
                    pagination={false}
                    showHeader
                    rowClassName="jobs-table-content"
                    onRow={(req) => ({ onClick: () => handleRowClick(req, record) })}
                />
            </SbJobsCodeViewStyled>
        );
    };

    // ── Row props for main table ───────────────────────────────────────────────
    // Parent rows represent job-code groups (no job_id). Clicking the row
    // expands/collapses the nested requisition list — same as clicking the arrow.

    const getRowProps = (record) => {
        const hasReqs = record.requisition_groups?.length > 0;
        return {
            title: hasReqs ? "Click to expand requisitions" : "No requisitions",
            className: "jobs-table-content",
            onClick: () => {
                if (hasReqs) toggleExpand(record.job_code);
            },
        };
    };

    return (
        <JobsTableWrapperStyled>
            <SbTable
                columns={columns}
                dataSource={filteredJobs}
                rowKey={(record, idx) => record.job_code ?? idx}
                loading={loading}
                sticky
                pagination={{
                    showTotal: (total) => {
                        const hasActiveFilters = total < (filteredJobs?.length ?? 0);
                        return (
                            <div className="paic-pagination-total">
                                <FilterOutlined />
                                <span className={hasActiveFilters ? "is-active-text" : "is-empty-text"}>
                                    {total} {total === 1 ? "Job Code" : "Job Codes"}
                                </span>
                            </div>
                        );
                    },
                }}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys: expandedRow != null ? [expandedRow] : [],
                    showExpandColumn: false,
                    rowExpandable: (record) => record.requisition_groups?.length > 0,
                }}
                onRow={getRowProps}
            />
        </JobsTableWrapperStyled>
    );
};

export default JobsCodeViewTableV2;
